import { FC, useState, useMemo, useEffect } from 'react';
import { X, ChevronRight, CheckCircle2, Info } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { CartData, useUpdateCartItemLens } from '../../../hooks/useCart';
import { usePublicLensProducts, useResolvePrescriptionGroup, PrescriptionDataInput } from '../../product/hooks/useLensPublic';
import { RxMismatchFallback } from './RxMismatchFallback';
import { PrescriptionUploader } from './PrescriptionUploader';
import { toast } from 'react-hot-toast';

interface PrescriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    cart: CartData;
    onCompleteFlow: () => void;
}

export const PrescriptionModal: FC<PrescriptionModalProps> = ({ isOpen, onClose, cart, onCompleteFlow }) => {
    const { t, i18n } = useTranslation('shop');
    const isRtl = i18n.dir() === 'rtl';

    const getTranslation = (prod: any) => {
        if (!prod?.translations?.length) return { name: prod?.feature || '', description: '' };
        const exact = prod.translations.find((t: any) => t.locale === i18n.language);
        return exact || prod.translations[0];
    };

    // 1. Identify items needing resolution
    const unresolvedItems = useMemo(() => {
        if (!cart?.items) return [];
        return cart.items.filter(item => {
            if (item.prescriptionData?.entryMethod === 'READING_GLASSES' || item.prescriptionData?.entryMethod === 'CONTACT_LENS_NO_RX') {
                return false;
            }
            if (item.prescriptionData?.entryMethod === 'CONTACT_LENS_RX') {
                const hasData = !!(item.prescriptionData.prescriptionImageUrl || item.prescriptionData.rightEye);
                return !hasData;
            }
            return !!item.product?.isPrescriptionReady && !item.lensVariantId && !item.frameOnly;
        });
    }, [cart]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const currentItem = unresolvedItems[currentIndex];

    // Wizard Step: 'CHOOSE' | 'RX_ENTRY' | 'LENS_SELECTION' | 'RX_MISMATCH'
    const [step, setStep] = useState<'CHOOSE' | 'RX_ENTRY' | 'LENS_SELECTION' | 'RX_MISMATCH'>('CHOOSE');

    // Rx Data State
    const [prescriptionImageUrl, setPrescriptionImageUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    
    const [rightEye, setRightEye] = useState<PrescriptionDataInput>({ sphere: 0, cylinder: 0, axis: 0 });
    const [leftEye, setLeftEye] = useState<PrescriptionDataInput>({ sphere: 0, cylinder: 0, axis: 0 });
    const [pd, setPd] = useState<number>(63);
    const [addPower, setAddPower] = useState<number | null>(null);

    // Lens Selection State
    const [selectedVisionType, setSelectedVisionType] = useState<string>('SINGLE_VISION');
    const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
    const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

    const updateItemLens = useUpdateCartItemLens();

    // Reset state when switching items
    useEffect(() => {
        if (currentItem?.prescriptionData?.entryMethod === 'CONTACT_LENS_RX') {
            setStep('RX_ENTRY');
        } else {
            setStep('CHOOSE');
        }
        setRightEye({ sphere: 0, cylinder: 0, axis: 0 });
        setLeftEye({ sphere: 0, cylinder: 0, axis: 0 });
        setPd(63);
        setAddPower(null);
        setPrescriptionImageUrl(null);
        setShowManualEntry(false);
        setSelectedVisionType('SINGLE_VISION');
        setSelectedFeature(null);
        setSelectedVariantId(null);
    }, [currentItem?.id]);

    // API Hooks
    const { data: lensProducts, isLoading: isLoadingLenses } = usePublicLensProducts();
    const { data: rxGroupData, isLoading: isResolvingGroup } = useResolvePrescriptionGroup(
        rightEye, leftEye, currentItem?.product?.id
    );
    // Unpack the { match, alternatives } envelope (backwards-compat with legacy flat shape)
    const rxGroupParams = rxGroupData?.match ?? (rxGroupData && 'groupNumber' in (rxGroupData as any) ? (rxGroupData as any) : null);
    const rxAlternatives = rxGroupData?.alternatives ?? [];
    const hasRxMismatch = rxGroupData !== undefined && rxGroupData !== null && rxGroupData.match === null;

    // Helpers for dropdowns
    const generateOptions = (min: number, max: number, stepAmount: number) => {
        const opts = [];
        for (let i = min; i <= max; i += stepAmount) {
            // Fix floating point errors
            const val = Math.round(i * 100) / 100;
            opts.push(val);
        }
        return opts;
    };
    const sphOptions = generateOptions(-20, 20, 0.25);
    const cylOptions = generateOptions(-10, 10, 0.25);

    const availableLenses = useMemo(() => {
        if (!lensProducts) return [];
        let activeLenses = lensProducts.filter((p: any) => p.isActive);

        if (rxGroupParams) {
            const ruleLensProducts = rxGroupParams.lensProducts ?? [];
            if (ruleLensProducts.length > 0) {
                const allowedIds = new Set(ruleLensProducts.map((p: any) => p.id));
                activeLenses = activeLenses.filter((p: any) => allowedIds.has(p.id));
            }
        }
        return activeLenses;
    }, [lensProducts, rxGroupParams]);

    const visionTypes = useMemo(() => {
        const types = new Set(availableLenses.map((p: any) => p.visionType));
        // Only show types that exist in the database for the selected frame
        types.add('FRAME_ONLY');
        
        const explicitOrder = ['SINGLE_VISION', 'READING', 'PROGRESSIVE', 'BIFOCAL', 'FRAME_ONLY'];
        return Array.from(types).sort((a: any, b: any) => {
            const indexA = explicitOrder.indexOf(a);
            const indexB = explicitOrder.indexOf(b);
            return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
        }) as string[];
    }, [availableLenses]);

    const featuresForVisionType = useMemo(() => {
        if (selectedVisionType === 'FRAME_ONLY') return [];
        return availableLenses.filter((p: any) => p.visionType === selectedVisionType);
    }, [availableLenses, selectedVisionType]);


    const handleFrameOnly = () => {
        if (!currentItem) return;
        updateItemLens.mutate({
            itemId: currentItem.id,
            payload: { frameOnly: true }
        }, {
            onSuccess: () => {
                if (currentIndex < unresolvedItems.length - 1) {
                    setCurrentIndex(prev => prev + 1);
                } else {
                    onCompleteFlow();
                }
            },
            onError: () => toast.error(t('cart.errorUpdating', 'Error updating item'))
        });
    };

    const handleConfirmContactLensRx = () => {
        if (!currentItem) return;
        // Image is always required now
        const rxPayload: any = {
            entryMethod: 'UPLOAD',
            prescriptionImageUrl,
            rightEye: { ...rightEye },
            leftEye: { ...leftEye },
        };

        updateItemLens.mutate({
            itemId: currentItem.id,
            payload: { prescriptionData: rxPayload }
        }, {
            onSuccess: () => {
                if (currentIndex < unresolvedItems.length - 1) {
                    setCurrentIndex(prev => prev + 1);
                } else {
                    onCompleteFlow();
                }
            },
            onError: () => toast.error(t('cart.errorUpdating', 'Error updating item'))
        });
    };

    const handleConfirmRx = () => {
        if (currentItem?.prescriptionData?.entryMethod === 'CONTACT_LENS_RX') {
            return handleConfirmContactLensRx();
        }

        if (hasRxMismatch) {
            // Rx doesn't fit any of this product's groups — show fallback
            setStep('RX_MISMATCH');
        } else {
            setStep('LENS_SELECTION');
        }
    };

    const handleConfirmLens = () => {
        if (!currentItem) return;

        if (selectedVisionType === 'FRAME_ONLY') {
            return handleFrameOnly();
        }

        if (!selectedVariantId) return;

        const featureProd = featuresForVisionType.find((p: any) => p.feature === selectedFeature);
        const variant = featureProd?.variants.find((v: any) => v.id === selectedVariantId);
        if (!variant) return;

        // Always send both image + manual values
        const rxPayload: any = {
            lensVariantId: variant.id,
            entryMethod: 'UPLOAD', // image always required now
            prescriptionImageUrl,
            suggestedIndex: rxGroupParams?.suggestedIndex,
            selectedIndex: variant.refractiveIndex,
            pd: pd,
            rightEye: { ...rightEye },
            leftEye: { ...leftEye },
            addPower: addPower,
            prescriptionGroupId: rxGroupParams?.groupNumber,
        };

        updateItemLens.mutate({
            itemId: currentItem.id,
            payload: {
                lensVariantId: variant.id,
                prescriptionData: rxPayload
            }
        }, {
            onSuccess: () => {
                if (currentIndex < unresolvedItems.length - 1) {
                    setCurrentIndex(prev => prev + 1);
                } else {
                    onCompleteFlow();
                }
            },
            onError: () => toast.error(t('cart.errorUpdating', 'Error updating item'))
        });
    };

    if (!isOpen || !currentItem) return null;

    // Derived price calculations for current item
    const framePrice = currentItem.variant?.price || currentItem.product.price;
    let selectedLensPrice = 0;
    if (step === 'LENS_SELECTION' && selectedVariantId && selectedVisionType !== 'FRAME_ONLY') {
        const featureProd = featuresForVisionType.find((p: any) => p.feature === selectedFeature);
        const variant = featureProd?.variants.find((v: any) => v.id === selectedVariantId);
        if (variant) selectedLensPrice = variant.price;
    }
    const totalPrice = framePrice + selectedLensPrice;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-6 pb-0 md:pb-6">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

            <div className={`relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl bg-white md:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200`}>

                {/* Header */}
                <div className="flex-none px-4 md:px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white z-10">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold tracking-tight text-gray-900">
                            {t('prescription.modalTitle', 'Configure Your Lenses')}
                        </h2>
                        {unresolvedItems.length > 1 && (
                            <p className="text-sm text-gray-500 font-medium mt-0.5">
                                {t('prescription.itemOf', 'Item {{current}} of {{total}}', { current: currentIndex + 1, total: unresolvedItems.length })}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className={`p-2 ${isRtl ? '-ml-2' : '-mr-2'} text-gray-400 hover:bg-gray-100 hover:text-gray-900 rounded-full transition-colors`}>
                        <X size={20} />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="flex-none h-1 bg-gray-100 w-full">
                    <div
                        className="h-full bg-primary transition-all duration-300 ease-out"
                        style={{ width: step === 'CHOOSE' ? '33%' : step === 'RX_ENTRY' ? '66%' : '100%' }}
                    />
                </div>

                {/* Body (Scrollable) */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 bg-gray-50/50">

                    {/* Product Summary Context */}
                    <div className="flex items-center gap-4 p-3 bg-white border border-gray-200 rounded-xl mb-6 shadow-sm">
                        <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                            <img
                                src={currentItem.product.images?.[0]?.url || 'https://placehold.co/100x100/F9FAFB/9CA3AF?text=Frame'}
                                alt={getTranslation(currentItem.product).name}
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 text-sm truncate">{getTranslation(currentItem.product).name}</h3>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{currentItem.variant?.name || t('prescription.standardVariant', 'Standard')}</p>
                        </div>
                        <div className={`font-bold text-primary ${isRtl ? 'pr-4 border-r' : 'pl-4 border-l'} border-gray-100`}>
                            {t('common.currency', 'EGP')} {framePrice.toLocaleString()}
                        </div>
                    </div>

                    <div className="relative">
                        {/* STEP 1: CHOOSE ACTION */}
                        {step === 'CHOOSE' && (
                            <div className="animate-in slide-in-from-right-8 fade-in duration-300 flex flex-col items-center justify-center py-8 space-y-8">
                                <h3 className="text-xl font-bold text-gray-900 text-center">
                                    {t('prescription.howToProceed', 'How would you like to proceed with this frame?')}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full px-2">
                                    {/* Add Lenses Option */}
                                    <button
                                        onClick={() => setStep('RX_ENTRY')}
                                        className="relative p-6 bg-blue-50/50 border-2 border-blue-200 rounded-2xl hover:border-blue-600 hover:shadow-md transition-all text-center flex flex-col items-center gap-4 group"
                                    >
                                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-blue-900">{t('prescription.addLensesTitle', 'Add Prescription Lenses')}</h4>
                                            <p className="text-sm text-blue-700/80 mt-1">{t('prescription.addLensesDesc', 'Configure custom lenses for your vision.')}</p>
                                        </div>
                                    </button>

                                    {/* Frame Only Option */}
                                    <button
                                        onClick={handleFrameOnly}
                                        className="relative p-6 bg-white border-2 border-gray-200 rounded-2xl hover:border-gray-900 hover:shadow-md transition-all text-center flex flex-col items-center gap-4 group"
                                    >
                                        <div className="w-16 h-16 bg-gray-50 text-gray-900 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v8" /><path d="M8 12h8" /></svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-lg text-gray-900">{t('prescription.frameOnlyTitle', 'Frame Only')}</h4>
                                            <p className="text-sm text-gray-500 mt-1">{t('prescription.frameOnlyDesc', 'Buy the frame without prescription lenses.')}</p>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: RX ENTRY */}
                        {step === 'RX_ENTRY' && (
                            <div className="animate-in slide-in-from-right-8 fade-in duration-300">
                                <div className="flex items-center gap-2 mb-4">
                                    <button onClick={() => setStep('CHOOSE')} className={`text-gray-400 hover:text-gray-900 transition-colors p-1 ${isRtl ? '-mr-1' : '-ml-1'}`}>
                                        <ChevronRight className={`w-5 h-5 ${isRtl ? '' : 'rotate-180'}`} />
                                    </button>
                                    <h3 className="text-base font-bold text-gray-900">{t('prescription.provideRx', 'Provide Your Prescription')}</h3>
                                </div>

                                {/* Upload Section (Required) */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-bold text-gray-900">
                                            {t('prescription.uploadTab', 'Upload Prescription')}
                                        </label>
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                            {t('prescription.uploadRequired', 'Required')}
                                        </span>
                                    </div>
                                    <PrescriptionUploader
                                        value={prescriptionImageUrl}
                                        onChange={setPrescriptionImageUrl}
                                        onUploading={setIsUploading}
                                    />
                                    {!prescriptionImageUrl && !isUploading && (
                                        <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                            <span>⚠️</span>
                                            {t('prescription.uploadRequiredHint', 'Upload a photo of your prescription to continue.')}
                                        </p>
                                    )}
                                </div>

                                {/* Manual Entry Section (Optional, collapsible) */}
                                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm mb-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowManualEntry(v => !v)}
                                        className="w-full flex items-center justify-between p-4 text-start hover:bg-gray-50/50 transition-colors"
                                    >
                                        <div>
                                            <p className="text-sm font-bold text-gray-900">
                                                {t('prescription.manualOptional', 'Also enter values manually (optional)')}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {t('prescription.manualOptionalHint', 'Helps us verify your prescription automatically.')}
                                            </p>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${showManualEntry ? 'rotate-90' : (isRtl ? 'rotate-180' : '')}`} />
                                    </button>

                                    {showManualEntry && (
                                        <div className="border-t border-gray-100 p-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-gray-50 rounded-xl p-3 space-y-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></span>
                                                        <span className="text-xs font-bold text-gray-900">{t('prescription.right', 'Right')}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">SPH</span>
                                                        <select value={rightEye.sphere} onChange={e => setRightEye({ ...rightEye, sphere: parseFloat(e.target.value) })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5">
                                                            {sphOptions.map(val => <option key={`od-sph-${val}`} value={val}>{val > 0 ? `+${val.toFixed(2)}` : val.toFixed(2)}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">CYL</span>
                                                        <select value={rightEye.cylinder} onChange={e => setRightEye({ ...rightEye, cylinder: parseFloat(e.target.value) })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5">
                                                            {cylOptions.map(val => <option key={`od-cyl-${val}`} value={val}>{val > 0 ? `+${val.toFixed(2)}` : val.toFixed(2)}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">AXIS</span>
                                                        <input type="number" min="0" max="180" step="1" placeholder="0-180" value={rightEye.axis || ''} onChange={e => setRightEye({ ...rightEye, axis: parseInt(e.target.value) || 0 })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5 placeholder-gray-300" />
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50 rounded-xl p-3 space-y-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                                                        <span className="text-xs font-bold text-gray-900">{t('prescription.left', 'Left')}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">SPH</span>
                                                        <select value={leftEye.sphere} onChange={e => setLeftEye({ ...leftEye, sphere: parseFloat(e.target.value) })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5">
                                                            {sphOptions.map(val => <option key={`os-sph-${val}`} value={val}>{val > 0 ? `+${val.toFixed(2)}` : val.toFixed(2)}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">CYL</span>
                                                        <select value={leftEye.cylinder} onChange={e => setLeftEye({ ...leftEye, cylinder: parseFloat(e.target.value) })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5">
                                                            {cylOptions.map(val => <option key={`os-cyl-${val}`} value={val}>{val > 0 ? `+${val.toFixed(2)}` : val.toFixed(2)}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-8">AXIS</span>
                                                        <input type="number" min="0" max="180" step="1" placeholder="0-180" value={leftEye.axis || ''} onChange={e => setLeftEye({ ...leftEye, axis: parseInt(e.target.value) || 0 })} className="flex-1 border border-gray-200 rounded-lg text-xs text-center font-medium focus:ring-primary focus:border-primary bg-white py-1.5 placeholder-gray-300" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-gray-50 rounded-xl p-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">IPD</span>
                                                        <span className="text-[10px] text-gray-400">mm</span>
                                                    </div>
                                                    <select value={pd} onChange={e => setPd(parseInt(e.target.value))} className="w-full border border-gray-200 rounded-lg text-sm text-center font-bold focus:ring-primary focus:border-primary bg-white py-1.5 text-gray-900">
                                                        {Array.from({ length: 31 }, (_, i) => 53 + i).map(val => (
                                                            <option key={`pd-${val}`} value={val}>{val}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="bg-gray-50 rounded-xl p-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">ADD</span>
                                                        <span className="text-[10px] text-gray-400">{t('prescription.optional', 'opt.')}</span>
                                                    </div>
                                                    <select value={addPower === null ? '' : addPower} onChange={e => setAddPower(e.target.value === '' ? null : parseFloat(e.target.value))} className="w-full border border-gray-200 rounded-lg text-sm text-center font-bold focus:ring-primary focus:border-primary bg-white py-1.5 text-gray-900">
                                                        <option value="">—</option>
                                                        {generateOptions(0.50, 3.50, 0.25).map(val => (
                                                            <option key={`add-${val}`} value={val}>+{val.toFixed(2)}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={handleConfirmRx}
                                    disabled={isUploading || !prescriptionImageUrl}
                                    className="w-full py-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {currentItem?.prescriptionData?.entryMethod === 'CONTACT_LENS_RX'
                                        ? t('common.saveAndContinue', 'Save & Continue')
                                        : t('prescription.continueToLenses', 'Continue to Lens Selection')}
                                    <ChevronRight className={`w-4 h-4 ${isRtl ? 'rotate-180' : ''}`} />
                                </button>
                            </div>
                        )}

                        {/* STEP 3: LENS SELECTION */}
                        {step === 'LENS_SELECTION' && (
                            <div className="animate-in slide-in-from-right-8 fade-in duration-300 pb-20 md:pb-0">
                                <div className="flex items-center gap-2 mb-4">
                                    <button onClick={() => setStep('RX_ENTRY')} className={`text-gray-400 hover:text-gray-900 transition-colors p-1 ${isRtl ? '-mr-1' : '-ml-1'}`}>
                                        <ChevronRight className={`w-5 h-5 ${isRtl ? '' : 'rotate-180'}`} />
                                    </button>
                                    <h3 className="text-base font-bold text-gray-900">{t('prescription.selectLenses', 'Select Your Lenses')}</h3>
                                </div>

                                {isLoadingLenses ? (
                                    <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                                        <div className="w-8 h-8 border-4 border-gray-200 border-t-primary rounded-full animate-spin mb-4"></div>
                                        <p className="text-sm font-medium">{t('prescription.loadingLenses', 'Loading lens options...')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-6">

                                        {/* Smart Recommendation Banner */}
                                        {!isResolvingGroup && rxGroupParams?.groupNumber && (
                                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 md:p-4 flex gap-3 text-start">
                                                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <h4 className="text-sm font-bold text-blue-900">{t('prescription.recommendedBadge', 'Recommended for your Rx')}</h4>
                                                    <p className="text-xs text-blue-800 mt-1 leading-relaxed">
                                                        <Trans
                                                            i18nKey="prescription.recommendationText"
                                                            t={t}
                                                            values={{
                                                                index: rxGroupParams.suggestedIndex,
                                                                note: i18n.language === 'ar' ? rxGroupParams.noteAr : rxGroupParams.noteEn
                                                            }}
                                                            components={[<strong key="1" />]}
                                                        />
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* 1. Vision Type */}
                                        <div className="space-y-3">
                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">1. {t('prescription.visionType', 'Vision Type')}</h4>
                                            <div className="space-y-3">
                                                {visionTypes.filter(vt => vt !== 'FRAME_ONLY').map(vt => {
                                                    const isSelected = selectedVisionType === vt;
                                                    return (
                                                        <button
                                                            key={vt}
                                                            onClick={() => {
                                                                setSelectedVisionType(vt);
                                                                setSelectedFeature(null);
                                                                setSelectedVariantId(null);
                                                            }}
                                                            className={`w-full p-4 rounded-xl border-2 transition-all flex items-start text-start gap-4 ${isSelected ? 'border-primary ring-1 ring-primary/10 bg-primary/5 shadow-sm' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`}
                                                        >
                                                            <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}>
                                                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <span className={`text-base font-bold ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{t(`prescription.visionTypes.${vt}`, vt.replace('_', ' '))}</span>
                                                                <span className="text-[13px] text-gray-500 leading-relaxed">
                                                                    {t(`prescription.visionTypeDescriptions.${vt}`, '')}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* 2. Lens Feature & Variants (Combined) */}
                                        {selectedVisionType && selectedVisionType !== 'FRAME_ONLY' && featuresForVisionType.length > 0 && (
                                            <div className="space-y-3 animate-in fade-in slide-in-from-top-4 duration-300 pb-2">
                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">2. {t('prescription.lensCoating', 'Lens Coating')} & {t('prescription.thickness', 'Thickness (Index)')}</h4>
                                                <div className="space-y-3">
                                                    {featuresForVisionType.map((prod: any, idx: number) => {
                                                        const tProd = getTranslation(prod);
                                                        const isSelected = selectedFeature === prod.feature;

                                                        return (
                                                            <div
                                                                key={prod.id}
                                                                className={`rounded-xl border-2 transition-all bg-white overflow-hidden ${isSelected ? 'border-primary ring-1 ring-primary/10 shadow-sm relative' : 'border-gray-200 hover:border-gray-300'}`}
                                                            >
                                                                <button
                                                                    onClick={() => {
                                                                        if (!isSelected) {
                                                                            setSelectedFeature(prod.feature);
                                                                            // Auto-select a valid variant to speed up checkout
                                                                            const activeVariants = prod.variants?.filter((v: any) => v.isActive) || [];
                                                                            if (activeVariants.length > 0) {
                                                                                const recommended = activeVariants.find((v: any) => rxGroupParams?.suggestedIndex != null && v.refractiveIndex >= rxGroupParams.suggestedIndex);
                                                                                setSelectedVariantId(recommended ? recommended.id : activeVariants[0].id);
                                                                            } else {
                                                                                setSelectedVariantId(null);
                                                                            }
                                                                        }
                                                                    }}
                                                                    className={`w-full text-start p-4 transition-colors ${isSelected ? 'bg-primary/5' : ''}`}
                                                                >
                                                                    <div className="flex justify-between items-start mb-1">
                                                                        <div className="flex items-center gap-4">
                                                                             {(prod.videoUrl || prod.imageUrl) && (
                                                                                 <div className="w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 bg-white p-1 md:p-2 shadow-sm flex items-center justify-center">
                                                                                     {prod.videoUrl ? (
                                                                                         <video src={prod.videoUrl} autoPlay loop muted playsInline className="w-full h-full object-contain drop-shadow-sm" />
                                                                                     ) : (
                                                                                         <img src={prod.imageUrl} alt={tProd.name} className="w-full h-full object-contain mix-blend-multiply drop-shadow-sm" />
                                                                                     )}
                                                                                 </div>
                                                                             )}
                                                                             <div className="flex flex-col">
                                                                                <span className="font-extrabold text-gray-900 text-lg md:text-xl leading-snug">{tProd.name}</span>
                                                                             </div>
                                                                         </div>
                                                                        {idx === 0 && <span className="text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full flex-shrink-0">{t('prescription.mostPopular', 'Most Popular')}</span>}
                                                                    </div>
                                                                    <p className="text-xs text-gray-500 leading-snug pe-8">{tProd.description}</p>
                                                                    {isSelected && (
                                                                        <div className={`absolute top-4 ${isRtl ? 'left-4' : 'right-4'} text-primary`}>
                                                                            <CheckCircle2 size={20} className="fill-primary/20" />
                                                                        </div>
                                                                    )}
                                                                </button>

                                                                {/* Variants Dropdown */}
                                                                {isSelected && prod.variants && prod.variants.filter((v: any) => v.isActive).length > 0 && (
                                                                    <div className="p-3 bg-gray-50/80 border-t border-gray-100 animate-in slide-in-from-top-2 fade-in duration-200">
                                                                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2 px-1">
                                                                            {t('prescription.thickness', 'Thickness (Index)')}
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                            {prod.variants.filter((v: any) => v.isActive).map((variant: any) => {
                                                                                const isRecommended = rxGroupParams?.suggestedIndex != null && variant.refractiveIndex >= rxGroupParams.suggestedIndex;
                                                                                const isTooThick = rxGroupParams?.suggestedIndex != null && variant.refractiveIndex < rxGroupParams.suggestedIndex;
                                                                                const isVariantSelected = selectedVariantId === variant.id;

                                                                                return (
                                                                                    <button
                                                                                        key={variant.id}
                                                                                        onClick={() => setSelectedVariantId(variant.id)}
                                                                                        className={`w-full text-start p-3 rounded-lg border transition-all flex items-center justify-between ${isVariantSelected ? 'border-primary bg-white shadow-sm ring-1 ring-primary/10' : 'border-gray-200 bg-white hover:border-gray-300'} ${isTooThick ? 'opacity-60' : ''}`}
                                                                                    >
                                                                                        <div>
                                                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                                                <span className={`font-bold text-sm ${isVariantSelected ? 'text-primary' : 'text-gray-900'}`}>{variant.tier}</span>
                                                                                                <span className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{t('prescription.idx', 'idx')} {variant.refractiveIndex}</span>
                                                                                                {!isTooThick && isRecommended && <span className="text-[10px] bg-blue-100 text-blue-700 font-medium px-1.5 py-0.5 rounded">{t('prescription.good', 'Good')}</span>}
                                                                                            </div>
                                                                                            <p className="text-[11px] text-gray-500">
                                                                                                {(variant.deliveryMinDays && variant.deliveryMaxDays)
                                                                                                    ? t('prescription.deliveryDays', '{{min}}\u2013{{max}} business days', { min: variant.deliveryMinDays, max: variant.deliveryMaxDays })
                                                                                                    : (variant.deliveryEstimate || '')}
                                                                                            </p>
                                                                                        </div>
                                                                                        <div className="text-end flex items-center gap-3">
                                                                                            <span className="font-bold text-gray-900 text-sm">+{t('common.currency', 'EGP')} {variant.price.toLocaleString()}</span>
                                                                                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 ${isVariantSelected ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                                                                                                {isVariantSelected && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                                                            </div>
                                                                                        </div>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Frame Only Option (Moved to bottom) */}
                                        {visionTypes.includes('FRAME_ONLY') && (
                                            <div className="pt-2 pb-6">
                                                <div className="flex items-center gap-4 mb-4">
                                                    <div className="h-px bg-gray-200 flex-1"></div>
                                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t('prescription.or', 'or')}</span>
                                                    <div className="h-px bg-gray-200 flex-1"></div>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedVisionType('FRAME_ONLY');
                                                        setSelectedFeature(null);
                                                        setSelectedVariantId(null);
                                                    }}
                                                    className={`w-full py-4 px-4 rounded-xl text-sm font-bold border transition-colors flex items-center justify-center gap-2 ${selectedVisionType === 'FRAME_ONLY' ? 'bg-gray-900 border-gray-900 text-white' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                                                >
                                                    {selectedVisionType === 'FRAME_ONLY' && <CheckCircle2 size={16} />}
                                                    {t('prescription.visionTypes.FRAME_ONLY', 'Frame Only (No Lenses)')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* STEP: RX MISMATCH FALLBACK */}
                        {step === 'RX_MISMATCH' && (
                            <RxMismatchFallback
                                alternatives={rxAlternatives}
                                onFrameOnly={handleFrameOnly}
                                isLoading={isResolvingGroup}
                            />
                        )}
                    </div>
                </div>

                {/* Footer (Sticky) */}
                {step === 'LENS_SELECTION' && (
                    <div className="flex-none p-4 md:p-6 bg-white border-t border-gray-100 shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">{t('prescription.total', 'Total')}</p>
                                <div className="text-xs text-gray-400">
                                    {t('prescription.frame', 'Frame:')} {framePrice.toLocaleString()} {selectedLensPrice > 0 && `+ ${t('prescription.lens', 'Lens:')} ${selectedLensPrice.toLocaleString()}`}
                                </div>
                            </div>
                            <div className="text-xl font-bold text-primary">
                                {t('common.currency', 'EGP')} {totalPrice.toLocaleString()}
                            </div>
                        </div>
                        <button
                            onClick={handleConfirmLens}
                            disabled={(selectedVisionType !== 'FRAME_ONLY' && !selectedVariantId) || updateItemLens.isPending}
                            className="w-full py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                        >
                            {updateItemLens.isPending ? t('prescription.saving', 'Saving...') : t('prescription.confirmLens', 'Confirm & Add Lenses')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
